import { buildOperation, PROXY_PARAMS } from "@/utils/hygraphQueries";

/**
 * Hygraph answers a permission failure with HTTP 200, an `errors` array AND a
 * `data` object whose collections are empty — e.g. a token with no read grant
 * returns `{ data: { articles: [] }, errors: [{ message: "not allowed" }] }`.
 * Checking only for `data` therefore reads a hard 403 as "no articles", which
 * renders an empty blog instead of an error and hides the real cause. Treat any
 * `errors` entry as a failure and put the reason in the message.
 */
function describeErrors(json) {
  return [...new Set((json.errors || []).map((e) => e.message))].join("; ");
}

/**
 * `allowPartial` exists for one caller: /blog-draft-preview.
 *
 * A DRAFT read needs a DRAFT grant on every related model, not just on Article.
 * The CMS token has Article but not Asset metadata, Author or SiteSettings, so a
 * draft article comes back as HTTP 200 with the article populated, those fields
 * nulled, and an `errors` entry per denied field. Failing hard there means the
 * reviewer sees the site's error page instead of the copy they are reviewing —
 * strictly worse for an internal tool than showing the text with a missing
 * avatar. Public reads stay strict: an empty blog must never be mistaken for a
 * successful response (see the 403-as-200 note above).
 *
 * Either way the reason is logged server-side, so a permission gap stays
 * visible instead of turning into a silently degraded page.
 */
function unwrap(json, { name, allowPartial } = {}) {
  const hasErrors = !!json?.errors?.length;

  if (hasErrors && allowPartial && json.data) {
    console.error(
      `[hygraph] ${name}: partial response, some fields denied — ${describeErrors(json)}`
    );
    return json.data;
  }
  if (hasErrors) throw new Error(`Hygraph: ${describeErrors(json)}`);
  if (!json?.data) throw new Error("Hygraph: empty response");
  return json.data;
}

/**
 * Runs a named Hygraph operation from utils/hygraphQueries.js.
 *
 * The route differs by where the code is executing, because the Hygraph
 * credential can write to the CMS and must not reach a browser:
 *
 *   server (SSR)  -> straight to Hygraph with the private token. This is the
 *                    only path that may request stage: DRAFT, and it is reached
 *                    only by /blog-draft-preview, which the rbac middleware
 *                    already restricts to dealer admins.
 *   browser        -> POST to our own /_hygraph proxy, which holds the token,
 *                    picks the query itself and forces stage: PUBLISHED.
 *
 * asyncData runs in both places — server on first load, browser on client-side
 * navigation — so both branches are live for the public blog.
 */
export async function hygraphOperation(name, params = {}, options = {}) {
  const { allowPartial = false } = options;

  if (process.server) {
    const endpoint = process.env.HYGRAPH_ENDPOINT;
    const token = process.env.HYGRAPH_AUTH_TOKEN;
    if (!endpoint || !token) throw new Error("Hygraph is not configured");

    const { query, variables } = buildOperation(name, params);
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, variables }),
    });
    return unwrap(await response.json(), { name, allowPartial });
  }

  // Browser: name the operation, never the query. `stage` and `draftsOnly` are
  // deliberately not sent — the proxy decides those.
  //
  // Anything else the caller passed is forwarded from PROXY_PARAMS rather than a
  // hand-written list, and an unrecognised param is an error instead of a silent
  // drop: dropping one used to leave the page rendering unfiltered or unpaged
  // data with no indication anything had gone wrong.
  const payload = { operation: name };
  const unsupported = [];

  for (const [key, value] of Object.entries(params)) {
    if (key === "stage" || key === "draftsOnly") continue;
    if (!PROXY_PARAMS.includes(key)) {
      unsupported.push(key);
      continue;
    }
    if (value !== undefined) payload[key] = value;
  }

  if (unsupported.length) {
    throw new Error(
      `Hygraph: ${name} cannot run in the browser — ${unsupported.join(", ")} ` +
        `not in PROXY_PARAMS. Add it there and validate it in server-middleware/hygraph.js.`
    );
  }

  const response = await fetch("/_hygraph", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return unwrap(await response.json(), { name, allowPartial });
}

export default {
  methods: {
    hygraph(name, params, options) {
      return hygraphOperation(name, params, options);
    },
  },
};
